import { useEffect, useMemo, useState } from "react";
import './style.css';
import { I18n } from 'react-redux-i18n';
import ChevronDown from '../../img/chevron-down-icon.png';
import ChevronUp from '../../img/chevron-up-icon.png';

function PaginatedTable({
  columns,
  filteredRows,
  pageSize,
  defaultSort = null,
  onRowClick = null
}) {
  const [page, setPage] = useState(1);
  const [sortConfig, setSortConfig] = useState(
    defaultSort ?? {
      key: null,
      direction: 'desc',
    }
  );
  const pageIndex = page - 1;
  const firstItemIndex = pageIndex * pageSize;

  const firstDisplayedItem = firstItemIndex + 1;
  const lastDisplayedItem = Math.min(page * pageSize, filteredRows.length);

  useEffect(() => {
    setPage(1);
  }, [filteredRows]);

  const sortedRows = useMemo(() => {
    if (!sortConfig.key) return filteredRows;
    const sorted = [...filteredRows].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (typeof aValue === 'number' && typeof bValue === "number") {
        return sortConfig.direction === "desc"
          ? bValue - aValue
          : aValue - bValue;
      }

      return sortConfig.direction === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    })

    return sorted; 
  }, [filteredRows, sortConfig]);

  const paginatedRows = useMemo(() => {
    return sortedRows.slice(firstItemIndex, firstItemIndex + pageSize);
  }, [sortedRows, firstItemIndex, pageSize]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));

  const orderTable = (key) => {
    setPage(1);
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "desc" ? "asc" : "desc",
        };
      }
      return {
        key,
        direction: 'asc',
      };
    })
  }

  return (
    <>
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.label}
                  className="cursor-pointer"
                  onClick={e => orderTable(col.accessor)}
                >
                  <div className="flex items-center justify-between gap-2">
                    {col.label}
                    {sortConfig.key === col.accessor ? (
                      sortConfig.direction === 'desc'
                      ? (
                        <img
                          src={ChevronDown}
                          className="chevron"
                          alt="desc"
                        />
                      ) : (
                        <img
                          src={ChevronUp}
                          className="chevron"
                          alt="asc"
                        />
                      )
                    ) : (
                      <img
                        src={ChevronDown}
                        className="chevron"
                        alt="asc"
                      />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(paginatedRows && paginatedRows.length > 0) ? (
              paginatedRows.map((row, i) => (
                <tr
                  key={i}
                  className={onRowClick ? 'clickable-row' : ''}
                  style={onRowClick ? { cursor: 'pointer' } : undefined}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}>
                  {columns.map((col) => (
                    <td key={col.label}>
                      {row[col.accessor]}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="empty-state">
                 {I18n.t('customerAccount.noDataAvailable')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="table-pagination">
        <div className="pagination-info">
          {filteredRows.length > 0 && (
            <>
              {firstDisplayedItem}–{lastDisplayedItem} sur {filteredRows.length}
            </>
          )}
        </div>

        <div className="pagination-actions">
          <button
            onClick={() => setPage((p) => Math.max(p - 1, 1))}
            disabled={page === 1}
          >
            ‹
          </button>

          <span>
            {page} / {totalPages || 1}
          </span>

          <button
            onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
            disabled={page >= totalPages}
          >
            ›
          </button>
        </div>
      </div>
    </>
  )
}

export default PaginatedTable;